<script setup lang="ts">
import { computed } from 'vue'
import { ExternalLink } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'

import { useMessageFailureText } from '../../composables/useMessageFailureText'
import { META_ERROR_CODES_URL } from '../../services/messageFailureReason'

export interface MessageFailureView {
  code: string | null
  reason: string | null
  /** Fecha del intento de envío, ya formateada; se muestra solo si viene. */
  sentAt?: string
}

const props = defineProps<{
  modelValue: boolean
  failure: MessageFailureView | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const { t } = useI18n()
const { hintFor } = useMessageFailureText()

const isVisible = computed({
  get: () => props.modelValue && props.failure !== null,
  set: (value) => emit('update:modelValue', value),
})

// Explicación en español de los códigos de Meta más comunes; el resto solo muestra
// el motivo que reporta Meta.
const hint = computed(() => hintFor(props.failure?.code))
</script>

<template>
  <AppDialog
    v-model="isVisible"
    :header="t('whatsapp.failure_title')"
    width="min(92vw, 480px)"
    :draggable="false"
  >
    <div v-if="failure" class="flex flex-col gap-3 text-sm">
      <div
        v-if="failure.sentAt"
        class="flex flex-col rounded-lg bg-slate-50 p-3 dark:bg-[var(--p-surface-800)]"
      >
        <span class="text-xs text-slate-400">{{ t('whatsapp.failure_sent_at') }}</span>
        <span class="font-medium text-slate-700 dark:text-slate-100">{{ failure.sentAt }}</span>
      </div>

      <div
        class="flex flex-col gap-2 rounded-lg border border-[#F18D9E] bg-red-50 p-3 dark:bg-red-950/20"
      >
        <template v-if="failure.reason || failure.code">
          <div v-if="failure.code" class="flex flex-col">
            <span class="text-xs text-[#D90A2C]">{{ t('whatsapp.failure_code') }}</span>
            <span class="font-semibold text-[#D90A2C]">{{ failure.code }}</span>
          </div>
          <div v-if="failure.reason" class="flex flex-col">
            <span class="text-xs text-[#D90A2C]">{{ t('whatsapp.failure_meta_reason') }}</span>
            <span class="break-words text-slate-700 dark:text-slate-100">{{ failure.reason }}</span>
          </div>
        </template>
        <span v-else class="text-slate-600 dark:text-slate-200">{{
          t('whatsapp.failure_no_reason')
        }}</span>
      </div>

      <div v-if="hint" class="flex flex-col">
        <span class="text-xs text-slate-400">{{ t('whatsapp.failure_meaning') }}</span>
        <span class="text-slate-700 dark:text-slate-100">{{ hint }}</span>
      </div>

      <a
        :href="META_ERROR_CODES_URL"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-xs font-medium text-[var(--p-primary-color)] hover:underline"
      >
        <ExternalLink :size="12" />
        {{ t('whatsapp.failure_docs_link') }}
      </a>
    </div>
  </AppDialog>
</template>
